import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { DropdownItem } from '../../components/input';
import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import { DocumentUploadFileState } from './documentUploadFileState';
import { DocumentUploadWorkflow } from './documentUploadGuidedTrainingState';
import { WarrantUploadState } from './warrantUploadState';

export type DocumentUploadPageParams = {
  teamId?: string;
};

const MY_UPLOADS_VALUE = 'my-uploads';

export class DocumentUploadPageState
  extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable(this, {
      selectedTeamId: observable,
      mode: observable,
      teamId: computed,
      team: computed,
      destinationItems: computed,
      destinationValue: computed,
      activeUploadState: computed,
      showStaging: computed,
      stagedCount: computed,
      mounted: action,
      unmounted: action,
      setSelectedTeamId: action,
      selectMode: action,
      setMode: action,
    });

    this.fileComponent = new DocumentUploadFileState(this.store, {
      teamId: () => this.teamId,
    });
    this.warrantComponent = new WarrantUploadState(this.store, {
      teamId: () => this.teamId,
    });
  }

  readonly fileComponent: DocumentUploadFileState;
  readonly warrantComponent: WarrantUploadState;

  selectedTeamId: string | null = null;
  mode: DocumentUploadWorkflow = 'warrant';

  private mountVersion = 0;

  get teamId(): string | undefined {
    return this.selectedTeamId ?? undefined;
  }

  get team() {
    return this.teamId ? this.store.teamManager.getTeam(this.teamId) : null;
  }

  get destinationItems(): DropdownItem[] {
    return [
      { value: MY_UPLOADS_VALUE, label: 'My Uploads' },
      ...this.store.teamManager.teamsSortedByName
        .filter(candidate => candidate.hasPermission('CreateJob'))
        .map(candidate => ({ value: candidate.id, label: candidate.displayName || candidate.name })),
    ];
  }

  get destinationValue(): string {
    return this.selectedTeamId ?? MY_UPLOADS_VALUE;
  }

  get activeUploadState() {
    return this.mode === 'warrant' ? this.warrantComponent : this.fileComponent;
  }

  get showStaging(): boolean {
    return this.mode === 'warrant'
      ? this.warrantComponent.showStagingForm
      : (this.fileComponent.showStagingForm || this.fileComponent.showProgressBox);
  }

  get stagedCount(): number {
    return this.mode === 'warrant'
      ? this.warrantComponent.stagedItems.length
      : this.fileComponent.stagedItems.length;
  }

  async mounted(params: DocumentUploadPageParams) {
    const currentMountVersion = ++this.mountVersion;
    this.selectedTeamId = params.teamId ?? null;
    this.mode = this.store.documentUploadGuidedTraining.getDefaultWorkflow();

    await this.store.teamManager.apiEnsureTeams();
    if (currentMountVersion !== this.mountVersion)
      return;

    if (!params.teamId) {
      const defaultLibraryId = this.resolveDefaultLibraryId();
      if (defaultLibraryId)
        runInAction(() => {
          if (currentMountVersion === this.mountVersion)
            this.selectedTeamId = defaultLibraryId;
        });
    }

    runInAction(() => {
      if (currentMountVersion === this.mountVersion)
        this.mode = this.store.documentUploadGuidedTraining.getDefaultWorkflow(this.team);
    });
    this.fileComponent.init();
    this.store.documentUploadGuidedTraining.registerUploadPage(this.guidedTrainingContext);
    this.store.documentUploadGuidedTraining.handleUploadPageMounted();
  }

  unmounted() {
    ++this.mountVersion;
    this.store.documentUploadGuidedTraining.handleUploadPageUnmounted();
    this.store.documentUploadGuidedTraining.unregisterUploadPage(this.guidedTrainingContext);
    this.fileComponent.reset();
    this.warrantComponent.reset();
    this.selectedTeamId = null;
    this.mode = this.store.documentUploadGuidedTraining.getDefaultWorkflow();
  }

  setSelectedTeamId(teamId: string | null) {
    this.selectedTeamId = teamId;
  }

  selectMode(workflow: DocumentUploadWorkflow) {
    this.mode = workflow;
    this.store.documentUploadGuidedTraining.handleWorkflowSelected(workflow);
  }

  setMode(workflow: DocumentUploadWorkflow) {
    this.mode = workflow;
  }

  handleBackButton(prevRoute: string | null) {
    if (prevRoute)
      window.history.back();
  }

  private resolveDefaultLibraryId(): string | null {
    const email = this.store.user?.email?.toLowerCase();
    if (!email)
      return null;

    return this.store.teamManager.teams.find(
      team => team.externalEmail?.toLowerCase() === email && team.hasPermission('CreateJob')
    )?.id ?? null;
  }

  private readonly guidedTrainingContext = {
    getTeamId: () => this.teamId ?? null,
    getWorkflow: () => this.mode,
    setWorkflow: (workflow: DocumentUploadWorkflow) => this.setMode(workflow),
    getTranslationState: () => this.fileComponent,
    getWarrantState: () => this.warrantComponent,
  };
}
